import React, { useEffect, useRef } from "react";
import { View, Text, StyleSheet, Animated, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";

/**
 * PRO WIZARD COMPONENT
 * - Sliding highlight bar
 * - Completed, current & upcoming states
 * - Icon animation
 * - Clickable steps
 * - RN & RN Web compatible
 */

const LeftWizard = ({ steps, activeStep, onStepPress }) => {
  const indicatorY = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.spring(indicatorY, {
      toValue: activeStep * 70, // row height
      speed: 12,
      bounciness: 7,
      useNativeDriver: true,
    }).start();
  }, [activeStep]);

  return (
    <View style={styles.wrapper}>

      {/* 🔥 Animated Purple Highlight Bar */}
      <Animated.View
        style={[
          styles.indicator,
          { transform: [{ translateY: indicatorY }] },
        ]}
      />

      {steps.map((step, index) => {
        const isActive = index === activeStep;
        const isCompleted = index < activeStep;

        return (
          <WizardStep
            key={index}
            index={index}
            step={step}
            active={isActive}
            completed={isCompleted}
            onPress={() => onStepPress?.(index)}
          />
        );
      })}
    </View>
  );
};

const WizardStep = ({ step, index, active, completed, onPress }) => {
  const scale = useRef(new Animated.Value(active ? 1 : 0.9)).current;
  const opacity = useRef(new Animated.Value(active ? 1 : completed ? 0.85 : 0.55)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(scale, {
        toValue: active ? 1 : 0.9,
        speed: 12,
        useNativeDriver: true,
      }),
      Animated.timing(opacity, {
        toValue: active ? 1 : completed ? 0.85 : 0.55,
        duration: 250,
        useNativeDriver: true,
      }),
    ]).start();
  }, [active, completed]);

  return (
    <TouchableOpacity onPress={onPress} activeOpacity={0.8}>
      <Animated.View style={[styles.row, { opacity }]}>

        {/* 🔘 ICON */}
        <Animated.View style={{ transform: [{ scale }] }}>
          {completed ? (
            <Ionicons name="checkmark-circle" size={28} color="#6C47FF" />
          ) : active ? (
            <Ionicons name="radio-button-on" size={28} color="#6C47FF" />
          ) : (
            <Ionicons name="radio-button-off" size={28} color="#C8C5D5" />
          )}
        </Animated.View>

        {/* TEXT LABELS */}
        <View style={{ flex: 1 }}>
          <Text
            style={[
              styles.label,
              { color: active || completed ? "#6C47FF" : "#8A82A3" },
            ]}
          >
            {step.label}
          </Text>

          <Text
            style={[
              styles.sub,
              {
                color: active
                  ? "#6C47FF"
                  : completed
                  ? "#8C8C8C"
                  : "#C8C5D5",
              },
            ]}
          >
            {step.subLabel}
          </Text>
        </View>

      </Animated.View>
    </TouchableOpacity>
  );
};

/* ⭐ PREMIUM STYLES ⭐ */
const styles = StyleSheet.create({
  wrapper: {
    width: "100%",
    paddingVertical: 10,
    paddingHorizontal: 12,
    position: "relative",
    flexDirection:'column'
  },

  indicator: {
    position: "absolute",
    left: 0,
    width: 4,
    height: 70, // matches row height
    backgroundColor: "#6C47FF",
    borderRadius: 10,
  },

  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 18,
    paddingHorizontal: 6,
    gap: 14,
    minHeight: 70, // required for indicator alignment
  },

  label: {
    fontSize: 15,
    fontWeight: "700",
    lineHeight: 18,
  },

  sub: {
    fontSize: 13,
    marginTop: 2,
    lineHeight: 16,
  },
});

export default LeftWizard;
